import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForecast } from '../hooks/useForecast'
import { useChangeNotice } from '../hooks/useChangeNotice'
import { C, F } from '../components/forecast/theme'
import { Icon, Btn } from '../components/forecast/prims'
import { OverviewTab } from '../components/forecast/OverviewTab'
import { BaselineTab } from '../components/forecast/BaselineTab'
import { ResearchTab } from '../components/forecast/ResearchTab'
import { ModelTab } from '../components/forecast/ModelTab'
import { CostsRevenueTab } from '../components/forecast/CostsRevenueTab'
import { ContractsTab } from '../components/forecast/ContractsTab'
import { TeamTab } from '../components/forecast/TeamTab'
import { CommitModal } from '../components/forecast/CommitModal'
import type { EventListItem } from '../types'

// EM-2 — Forecast Studio shell: a candidate-ideas rail + sub-tab bar routing to
// the five modelling tabs, and a header with the event name + Commit to plan.
// Route: /forecast (rail + empty) and /forecast/:id (a specific idea). Each tab
// self-fetches via useForecast(eventId) (react-query dedupes the shared query).

type TabKey = 'overview' | 'baseline' | 'research' | 'model' | 'costs' | 'contracts' | 'team'
const TABS: Array<[TabKey, string, string]> = [
  ['overview', 'Overview', 'eye'],
  ['baseline', 'Baseline & data', 'layers'],
  ['research', 'Market research', 'sparkle'],
  ['model', 'Model & scenarios', 'bolt'],
  ['costs', 'Costs & revenue', 'pound'],
  ['contracts', 'Contracts', 'file'],
  ['team', 'Team & approval', 'users']
]

export function ForecastStudioPage() {
  const params = useParams()
  const eventId = Number(params.id)
  const hasId = Number.isFinite(eventId) && eventId > 0
  const nav = useNavigate()

  const { ideas, ev, fc, notFound, startForecast } = useForecast(hasId ? eventId : 0)
  const { notice } = useChangeNotice(hasId ? eventId : 0)
  const [tab, setTab] = useState<TabKey>('overview')
  const [commitOpen, setCommitOpen] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)

  const cnSignTotal = notice?.signoff.length ?? 0
  const cnSignApproved = notice?.signoff.filter((s) => s.status === 'approved').length ?? 0
  const cnBlocking = cnSignTotal > 0 && cnSignApproved < cnSignTotal
  const teamBlocking = !!fc && fc.team.length > 0 && fc.team.filter((t) => t.approvalStatus === 'approved').length < fc.team.length

  function tryCommit() {
    if (!fc) return
    if (teamBlocking) {
      setTab('team')
      const remaining = fc.team.length - fc.team.filter((t) => t.approvalStatus === 'approved').length
      setFlash(`${remaining} stakeholder${remaining === 1 ? '' : 's'} still to sign off — all functions must approve before commit.`)
      setTimeout(() => setFlash(null), 5000)
      return
    }
    if (cnBlocking) {
      setTab('team')
      const remaining = cnSignTotal - cnSignApproved
      setFlash(`The change notice still needs sign-off — ${remaining} signator${remaining === 1 ? 'y' : 'ies'} to approve before commit.`)
      setTimeout(() => setFlash(null), 5000)
      return
    }
    setCommitOpen(true)
  }

  return (
    // Full-bleed inside the AppShell content area: negate its padding
    // (px-8 pt-7 pb-14 = 32/28/56) and fill viewport minus the 64px topbar.
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', minHeight: 420, margin: '-28px -32px -56px', fontFamily: F.body }}>
      {/* idea rail */}
      <aside style={{ width: 224, flexShrink: 0, borderRight: `1px solid ${C.line}`, background: C.paper, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${C.lineSoft}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="chart" size={17} color={C.green} />
            <span style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink }}>Candidate ideas</span>
          </div>
          <div style={{ fontFamily: F.body, fontSize: 11, color: C.inkFaint, marginTop: 3 }}>Model before committing to the plan</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 11, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ideas.length === 0 && <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkFaint, padding: 6 }}>No ideas yet. Create one from Events → Ideas.</div>}
          {ideas.map((e) => <IdeaRailButton key={e.id} e={e} active={e.id === eventId} onClick={() => nav(`/forecast/${e.id}`)} />)}
        </div>
      </aside>

      {/* canvas */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!hasId && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, background: C.parchment, textAlign: 'center', padding: 24 }}>
            <Icon name="chart" size={34} color={C.inkFaint} />
            <div style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 800, color: C.ink }}>Forecast Studio</div>
            <div style={{ fontFamily: F.body, fontSize: 13.5, color: C.inkSoft, maxWidth: 420 }}>Pick a candidate idea from the rail to model its viability, or create a new idea from the Events list.</div>
          </div>
        )}

        {hasId && (
          <>
            <div style={{ padding: '14px 26px 0', borderBottom: `1px solid ${C.line}`, background: C.card }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: C.brass }}>Forecast Studio</div>
                  <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 800, color: C.ink, margin: '2px 0 4px' }}>{ev?.name ?? 'Loading…'}</div>
                </div>
                <div style={{ paddingBottom: 10 }}>
                  <Btn variant="primary" small icon="check" onClick={tryCommit} disabled={!fc || fc.status === 'committed' || teamBlocking || cnBlocking}>
                    {fc?.status === 'committed' ? 'Committed' : 'Commit to plan'}
                  </Btn>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {TABS.map(([id, label, icon]) => (
                  <button key={id} onClick={() => setTab(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '11px 15px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: `2.5px solid ${tab === id ? C.green : 'transparent'}`, marginBottom: -1, fontFamily: F.body, fontSize: 13.5, fontWeight: tab === id ? 800 : 600, color: tab === id ? C.green : C.inkSoft }}>
                    <Icon name={icon} size={15} />{label}
                  </button>
                ))}
              </div>
            </div>

            {flash && (
              <div style={{ margin: '12px 26px 0', padding: '10px 14px', borderRadius: 9, background: C.oxbloodSoft, color: C.oxblood, fontFamily: F.body, fontSize: 13, fontWeight: 700, border: `1px solid ${C.oxblood}44` }}>{flash}</div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '22px 26px', background: C.parchment }}>
              {notFound ? (
                <StartForecastPrompt onStart={() => startForecast()} />
              ) : (
                <>
                  {tab === 'overview' && <OverviewTab eventId={eventId} onGoto={setTab} />}
                  {tab === 'baseline' && <BaselineTab eventId={eventId} />}
                  {tab === 'research' && <ResearchTab eventId={eventId} />}
                  {tab === 'model' && <ModelTab eventId={eventId} />}
                  {tab === 'costs' && <CostsRevenueTab eventId={eventId} />}
                  {tab === 'contracts' && <ContractsTab eventId={eventId} />}
                  {tab === 'team' && <TeamTab eventId={eventId} />}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {commitOpen && ev && (
        <CommitModal eventId={eventId} ev={ev} onClose={() => setCommitOpen(false)} />
      )}
    </div>
  )
}

function IdeaRailButton({ e, active, onClick }: { e: EventListItem; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ textAlign: 'left', border: `1px solid ${active ? C.green : C.lineSoft}`, background: active ? C.greenBg : C.card, borderRadius: 10, padding: '10px 11px', cursor: 'pointer' }}>
      <div style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: active ? C.green : C.inkFaint }}>{e.type.name}</div>
      <div style={{ fontFamily: F.serif, fontSize: 14.5, fontWeight: 700, color: C.ink, lineHeight: 1.15, marginTop: 3 }}>{e.name}</div>
    </button>
  )
}

function StartForecastPrompt({ onStart }: { onStart: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, padding: 40, textAlign: 'center' }}>
      <Icon name="sparkle" size={30} color={C.brass} />
      <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 800, color: C.ink }}>Start modelling this idea</div>
      <div style={{ fontFamily: F.body, fontSize: 13.5, color: C.inkSoft, maxWidth: 420 }}>Create a forecast to set the proposed slot, link comparable events, run market research, flex the model, and build the delivery team.</div>
      <Btn variant="primary" icon="plus" onClick={onStart}>Start forecast</Btn>
    </div>
  )
}
